/**
 * Thumbnail Scheduler — debounce / dedup / hash 동작 단위 테스트.
 *
 * 실제 puppeteer / DB 의존성은 dep injection (_setSchedulerDeps) 로 mock.
 * 검증 대상은 *스케줄링 정책 그 자체*:
 *   1) fnv1a hash 결정성 / 충돌 적음
 *   2) 동일 model 반복 schedule → generateThumbnail 1 회만
 *   3) per-board debounce — 빈번 schedule 시 중간 model 은 skip, 최신만 처리
 *   4) 다른 board 는 서로 독립
 *   5) generateThumbnail 실패 시 hash unset → 다음 schedule 에서 재시도
 *   6) saveThumbnail 실패도 동일 — 재시도 가능
 *   7) timestamp 가 saveThumbnail 인자로 정확히 전달됨
 */

// scheduler 모듈 import 시 끌려오는 무거운 deps (entity, puppeteer, shell DB)
// 는 _setSchedulerDeps 로 어차피 교체하니, import 시점 부담만 줄이고자 mock.
jest.mock('../../controllers/thumbnail', () => ({
  thumbnail: jest.fn().mockResolvedValue(undefined)
}))
jest.mock('./board', () => ({ Board: class MockBoard {} }))
jest.mock('@things-factory/shell', () => ({
  getDataSource: () => ({ transaction: async (cb: any) => cb({}) }),
  getRepository: () => ({ save: jest.fn().mockResolvedValue({}) })
}))

import {
  fnv1a,
  scheduleThumbnail,
  _setSchedulerDeps,
  _resetSchedulerDeps,
  _resetSchedulerState,
  _waitForIdle,
  getSchedulerStats,
  type SchedulerDeps
} from './thumbnail-scheduler'

// ─── fnv1a hash ─────────────────────────────────────────────────────────────

describe('fnv1a hash', () => {
  test('같은 입력은 항상 같은 hash', () => {
    expect(fnv1a('hello')).toBe(fnv1a('hello'))
    expect(fnv1a('{"x":1}')).toBe(fnv1a('{"x":1}'))
    expect(fnv1a('')).toBe(fnv1a(''))
  })

  test('다른 입력은 다른 hash (현실적인 model 변경)', () => {
    expect(fnv1a('{"width":100}')).not.toBe(fnv1a('{"width":200}'))
    expect(fnv1a('{"a":1}')).not.toBe(fnv1a('{"a":2}'))
    expect(fnv1a('A')).not.toBe(fnv1a('B'))
  })

  test('빈 문자열은 결정적 hash', () => {
    const h = fnv1a('')
    expect(typeof h).toBe('string')
    expect(h.length).toBeGreaterThan(0)
  })

  test('아주 작은 변경도 다른 hash', () => {
    expect(fnv1a('{"x":1.0}')).not.toBe(fnv1a('{"x":1.1}'))
    expect(fnv1a('abc')).not.toBe(fnv1a('abd'))
  })

  test('긴 입력에서도 빠르게 결정적', () => {
    const big = JSON.stringify({ components: Array(10000).fill({ type: 'rect', x: 1, y: 2 }) })
    const h1 = fnv1a(big)
    const h2 = fnv1a(big)
    expect(h1).toBe(h2)
  })

  test('hex 형식 string 반환', () => {
    const h = fnv1a('test')
    expect(h).toMatch(/^[0-9a-f]+$/)
  })

  test('100 개 다른 입력 중 hash 충돌 없음', () => {
    const hashes = new Set<string>()
    for (let i = 0; i < 100; i++) {
      hashes.add(fnv1a(`{"id":${i},"data":"value-${i}"}`))
    }
    expect(hashes.size).toBe(100)
  })
})

// ─── scheduleThumbnail 동작 ─────────────────────────────────────────────────

describe('scheduleThumbnail — debounce / dedup / hash', () => {
  let generateCalls: Array<{ model: string; context: any }>
  let saveCalls: Array<{ boardId: string; thumbnail: string; timestamp: Date }>
  let nextGenerateResult: string | undefined
  let generateShouldFail: boolean
  let saveShouldFail: boolean

  beforeEach(() => {
    generateCalls = []
    saveCalls = []
    nextGenerateResult = 'base64data'
    generateShouldFail = false
    saveShouldFail = false

    const mockDeps: SchedulerDeps = {
      generateThumbnail: async (model, context) => {
        generateCalls.push({ model, context })
        if (generateShouldFail) throw new Error('generate failed')
        return nextGenerateResult
      },
      saveThumbnail: async (boardId, thumbnail, timestamp) => {
        if (saveShouldFail) throw new Error('save failed')
        saveCalls.push({ boardId, thumbnail, timestamp })
      }
    }

    _setSchedulerDeps(mockDeps)
    _resetSchedulerState()
  })

  afterEach(() => {
    _resetSchedulerDeps()
    _resetSchedulerState()
  })

  test('첫 schedule → generateThumbnail 1 회, saveThumbnail 1 회', async () => {
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()

    expect(generateCalls).toHaveLength(1)
    expect(saveCalls).toHaveLength(1)
    expect(saveCalls[0].boardId).toBe('board-1')
    expect(saveCalls[0].thumbnail).toBe('data:image/png;base64,base64data')
  })

  test('saveThumbnail 인자에 dataURL 형식 적용', async () => {
    nextGenerateResult = 'AAAA'
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()

    expect(saveCalls[0].thumbnail).toBe('data:image/png;base64,AAAA')
  })

  test('generateThumbnail 가 null 반환 → empty thumbnail dataURL', async () => {
    nextGenerateResult = 'null'
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()

    expect(saveCalls[0].thumbnail).toMatch(/^data:image\/gif;base64,/)
  })

  test('generateThumbnail 가 undefined 반환 → empty thumbnail', async () => {
    nextGenerateResult = undefined
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()

    expect(saveCalls[0].thumbnail).toMatch(/^data:image\/gif;base64,/)
  })

  test('saveThumbnail timestamp 가 Date 인스턴스', async () => {
    const before = new Date()
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()
    const after = new Date()

    expect(saveCalls[0].timestamp).toBeInstanceOf(Date)
    expect(saveCalls[0].timestamp.getTime()).toBeGreaterThanOrEqual(before.getTime())
    expect(saveCalls[0].timestamp.getTime()).toBeLessThanOrEqual(after.getTime())
  })

  test('동일 model 두 번 schedule → 두 번째는 hash dedup 으로 skip', async () => {
    const sameModel = '{"w":100,"h":200}'

    scheduleThumbnail('board-1', sameModel, {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(1)

    scheduleThumbnail('board-1', sameModel, {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(1)  // hash dedup
  })

  test('model 변경 후 schedule → generateThumbnail 다시 호출', async () => {
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(1)

    scheduleThumbnail('board-1', '{"w":200}', {})  // 다른 model
    await _waitForIdle()
    expect(generateCalls).toHaveLength(2)
  })

  test('빈 boardId 또는 빈 model 은 no-op', async () => {
    scheduleThumbnail('', '{"w":100}', {})
    scheduleThumbnail('board-1', '', {})
    scheduleThumbnail('', '', {})
    await _waitForIdle()

    expect(generateCalls).toHaveLength(0)
    expect(saveCalls).toHaveLength(0)
  })

  test('빈번한 schedule (5 회) → debounce 로 generateThumbnail 최대 2 회 (진행 중 1 + 최신 1)', async () => {
    // 첫 schedule 은 즉시 진행. 그 사이 나머지 4 개는 pending 큐에 누적되어
    // 최신 model 1 개만 살아남고 그것이 두 번째 호출이 됨. 총 generate = 2.
    let resolveFirst: (value: string) => void
    let firstStarted = false

    _setSchedulerDeps({
      generateThumbnail: async (model, context) => {
        generateCalls.push({ model, context })
        if (!firstStarted) {
          firstStarted = true
          return new Promise<string>(resolve => { resolveFirst = resolve })
        }
        return 'base64data'
      },
      saveThumbnail: async (boardId, thumbnail, timestamp) => {
        saveCalls.push({ boardId, thumbnail, timestamp })
      }
    })

    // 5 연속 schedule (각각 다른 model)
    scheduleThumbnail('board-1', '{"v":1}', {})
    await new Promise(r => setImmediate(r))  // 첫 호출 시작 보장
    scheduleThumbnail('board-1', '{"v":2}', {})
    scheduleThumbnail('board-1', '{"v":3}', {})
    scheduleThumbnail('board-1', '{"v":4}', {})
    scheduleThumbnail('board-1', '{"v":5}', {})

    // 첫 호출 release
    resolveFirst!('base64data')
    await _waitForIdle()

    // 첫 호출 + 큐의 최신 (v=5) = 2 회
    expect(generateCalls).toHaveLength(2)
    expect(generateCalls[0].model).toBe('{"v":1}')
    expect(generateCalls[1].model).toBe('{"v":5}')
  })

  test('다른 board 는 서로 독립 처리', async () => {
    scheduleThumbnail('board-1', '{"v":1}', {})
    scheduleThumbnail('board-2', '{"v":1}', {})
    scheduleThumbnail('board-3', '{"v":1}', {})
    await _waitForIdle()

    expect(generateCalls).toHaveLength(3)
    expect(saveCalls.map(s => s.boardId).sort()).toEqual(['board-1', 'board-2', 'board-3'])
  })

  test('generateThumbnail 실패 시 hash unset → 다음 schedule 에서 재시도', async () => {
    generateShouldFail = true
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(1)
    expect(saveCalls).toHaveLength(0)

    // 같은 model 로 다시 — 실패했으니 hash 없어서 재시도
    generateShouldFail = false
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(2)
    expect(saveCalls).toHaveLength(1)
  })

  test('saveThumbnail 실패 시 hash unset → 다음 schedule 에서 재시도', async () => {
    saveShouldFail = true
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(1)
    expect(saveCalls).toHaveLength(0)

    saveShouldFail = false
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(2)
    expect(saveCalls).toHaveLength(1)
  })

  test('성공 → 같은 model 재시도 안 함 (hash set 됨)', async () => {
    scheduleThumbnail('board-1', '{"w":100}', {})
    await _waitForIdle()

    for (let i = 0; i < 5; i++) {
      scheduleThumbnail('board-1', '{"w":100}', {})
    }
    await _waitForIdle()

    expect(generateCalls).toHaveLength(1)  // 첫 성공 후 모두 dedup
  })

  test('성공 → 다른 model → 같은 첫 model 다시: 다시 호출됨 (hash 는 마지막 성공 model 만 추적)', async () => {
    scheduleThumbnail('board-1', '{"v":1}', {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(1)

    scheduleThumbnail('board-1', '{"v":2}', {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(2)

    // v=1 으로 돌아가도 마지막 hash 는 v=2 이므로 다시 호출.
    scheduleThumbnail('board-1', '{"v":1}', {})
    await _waitForIdle()
    expect(generateCalls).toHaveLength(3)
  })

  test('context 객체가 generateThumbnail 에 전달됨', async () => {
    const ctx = { state: { domain: { subdomain: 'test' }, user: { id: 'u1' } } }
    scheduleThumbnail('board-1', '{"w":100}', ctx)
    await _waitForIdle()

    expect(generateCalls[0].context).toBe(ctx)
  })

  test('진행 중에 새 schedule → 큐에 보존 → 끝나면 자동으로 처리', async () => {
    let resolveFirst: (value: string) => void
    const firstPromise = new Promise<string>(resolve => { resolveFirst = resolve })
    let callCount = 0

    _setSchedulerDeps({
      generateThumbnail: async (model, context) => {
        callCount++
        generateCalls.push({ model, context })
        if (callCount === 1) return firstPromise
        return 'base64data'
      },
      saveThumbnail: async (boardId, thumbnail, timestamp) => {
        saveCalls.push({ boardId, thumbnail, timestamp })
      }
    })

    scheduleThumbnail('board-1', '{"v":1}', {})
    await new Promise(r => setImmediate(r))
    expect(generateCalls).toHaveLength(1)

    // 진행 중에 새 schedule
    scheduleThumbnail('board-1', '{"v":2}', {})
    expect(generateCalls).toHaveLength(1)  // 아직 1 (대기 중)

    // 첫 호출 release
    resolveFirst!('first')
    await _waitForIdle()

    // 첫 호출 끝나고 큐의 v=2 처리됨
    expect(generateCalls).toHaveLength(2)
    expect(generateCalls[1].model).toBe('{"v":2}')
  })

  test('getSchedulerStats 는 합리적 값 반환', async () => {
    const stats = getSchedulerStats()
    expect(typeof stats.pending).toBe('number')
    expect(typeof stats.inFlight).toBe('number')
    expect(typeof stats.cachedHashes).toBe('number')

    scheduleThumbnail('board-1', '{"v":1}', {})
    await _waitForIdle()
    const after = getSchedulerStats()
    expect(after.cachedHashes).toBeGreaterThanOrEqual(1)
  })
})
